import axios from "axios";
import { notifyApiFailure, notifyApiSuccess } from "@/helpers/apiStatus";
import { getLoginRoute, roleOf } from "@/helpers/routeAccess.mjs";

const api = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || "http://localhost:5293/api",
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

// Request interceptor — attach JWT + Company header
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jwt");
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  const companyId = localStorage.getItem("companyId");
  const hasExplicitCompany = Object.keys(config.headers).some(
    (k) => k.toLowerCase() === "x-company-id"
  );
  if (companyId && !hasExplicitCompany) {
    config.headers["X-Company-Id"] = companyId;
  }
  return config;
});

// Response interceptor — handle 401
//
// A 401 from a login/OTP/register/forgot-password endpoint is "wrong
// credentials", not "your session expired" — wiping localStorage and
// redirecting on those would blow away the page (and the error toast)
// before the user could read why their login failed. Only treat a 401
// from any other authenticated endpoint as a session-expired signal.
// The /jc-auth entries are the anonymous routes of JcAuthController.
// /auth/logout is listed because a 401 there only means the session being
// ended was already over — sign-out is in progress and must not reload the page.
const AUTH_ENDPOINTS = [
  "/jc-auth/login",
  "/jc-auth/send-otp",
  "/jc-auth/verify-otp",
  "/jc-auth/verify-otp-login",
  "/jc-auth/register/",
  "/jc-auth/forgot-password",
  "/jc-auth/refresh",
  "/auth/login",
  "/auth/register",
  "/auth/logout",
];

function isAuthEndpoint(url) {
  if (!url) return false;
  return AUTH_ENDPOINTS.some((ep) => url.includes(ep));
}

// The IP rate limiter answers 429 with an empty body. Screens show
// err.response.data.message, so give them one instead of their generic fallback.
const TOO_MANY_REQUESTS_MESSAGE = "Too many requests. Please wait a minute and try again.";

function fillRateLimitMessage(error) {
  const response = error.response;
  if (!response || response.status !== 429) return;
  const data = response.data;
  const isObject = data !== null && typeof data === "object";
  if (isObject && typeof data.message === "string" && data.message.trim()) return;
  response.data = { ...(isObject ? data : {}), message: TOO_MANY_REQUESTS_MESSAGE };
  error.message = TOO_MANY_REQUESTS_MESSAGE;
}

api.interceptors.response.use(
  (response) => {
    notifyApiSuccess();
    return response;
  },
  (error) => {
// Deploy / outage detection — helpers/apiStatus.js probes /health and shows one
// global notice while the API is unreachable. A failed request never shows anything
// by itself; opt a request out with `meta: { ignoreOutage: true }`.
    notifyApiFailure(error);
    fillRateLimitMessage(error);

    if (error.response && error.response.status === 401 && !isAuthEndpoint(error.config?.url)) {
      // Capture role before wiping localStorage so we can send the user back
      // to the matching login (employer vs seeker).
      let role = "";
      try {
        role = roleOf(JSON.parse(localStorage.getItem("userdata") || "null"));
      } catch {
        role = "";
      }

      localStorage.removeItem("jwt");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("user");
      localStorage.removeItem("userdata");
      localStorage.removeItem("companyId");

      window.location.href = getLoginRoute(role);
    }
    return Promise.reject(error);
  }
);

export default api;
